import { Delete, Folder, KeyboardArrowDown, Sell } from '@mui/icons-material';
import { Divider, ListItemIcon, ListItemText, Menu } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDeleteShow } from '~/api/shows';
import { BulkActionButton, BulkActionMenuItem } from '~/components/bulk-actions';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { focusableDisabledItems } from '~/components/menu-item';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useBulkActionIds } from '~/hooks/unique-id';

export type BulkActionsProps = {
  selectedIds: GridRowSelectionModel;
  onMoveShow: () => void;
  onTagShow: () => void;
  canDestroySelectedShows?: boolean;
};

export const BulkActions = ({
  selectedIds,
  onMoveShow,
  onTagShow,
  canDestroySelectedShows,
}: BulkActionsProps) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { buttonId, menuId } = useBulkActionIds();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const [deleteShow] = useDeleteShow();

  if (!selectedIds.length) return null;

  return (
    <>
      <BulkActionButton
        ref={buttonRef}
        disableRipple
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={() => setOpen(true)}
      >
        {t('common.buttons.bulk_actions')}
        <KeyboardArrowDown />
      </BulkActionButton>
      <Menu
        id={menuId}
        anchorEl={buttonRef.current}
        open={open}
        onClose={() => setOpen(false)}
        MenuListProps={{
          ...focusableDisabledItems,
          'aria-labelledby': buttonId,
        }}
      >
        <BulkActionMenuItem
          onClick={() => {
            onMoveShow();
            setOpen(false);
          }}
        >
          <ListItemIcon>
            <Folder fontSize="small" />
          </ListItemIcon>
          {t('pages.shows.list.bulk_actions.move_to_folder')}
        </BulkActionMenuItem>
        <BulkActionMenuItem
          onClick={() => {
            onTagShow();
            setOpen(false);
          }}
        >
          <ListItemIcon>
            <Sell fontSize="small" />
          </ListItemIcon>
          {t('common.tags.add_tag')}
        </BulkActionMenuItem>
        <Divider />
        <BulkActionMenuItem
          tooltip={
            !canDestroySelectedShows
              ? t('pages.shows.list.bulk_actions.cannot_delete_default_show')
              : ''
          }
          disabled={!canDestroySelectedShows}
          onClick={async () => {
            if (!canDestroySelectedShows) return;
            setOpen(false);
            if (!(await confirmDelete())) return;
            await deleteShow({
              variables: {
                showIds: selectedIds.map((x) => +x),
              },
            });
          }}
        >
          <ListItemIcon>
            <Delete color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error' }}>
            {t('common.actions.delete')}
          </ListItemText>
        </BulkActionMenuItem>
      </Menu>

      <ConfirmDialog
        {...confirmDeleteProps}
        title={t('pages.shows.list.delete_dialog.title')}
        prompt={<Trans i18nKey="pages.shows.list.delete_dialog.prompt" />}
        confirm={t('common.dialogs.permanently_delete')}
        cancel={t('common.dialogs.cancel')}
        deleteConfirm
      />
    </>
  );
};
